import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const IntegrationTab = () => {
  const [integrations, setIntegrations] = useState([
    {
      id: 1,
      name: "SAP ERP",
      type: "ERP System",
      status: "connected",
      lastSync: "2025-08-19T04:30:00Z",
      endpoint: "https://sap.techcorp.com/api/v1",
      apiKey: "sap_***************",
      syncFrequency: "hourly",
      isEnabled: true,
      errorCount: 0
    },
    {
      id: 2,
      name: "Procurement Portal",
      type: "Procurement Platform",
      status: "connected",
      lastSync: "2025-08-19T04:15:00Z",
      endpoint: "https://procurement.techcorp.com/api",
      apiKey: "proc_***************",
      syncFrequency: "daily",
      isEnabled: true,
      errorCount: 0
    },
    {
      id: 3,
      name: "Barcode Scanner App",
      type: "Mobile Application",
      status: "error",
      lastSync: "2025-08-18T22:45:00Z",
      endpoint: "https://mobile-api.techcorp.com/scan",
      apiKey: "scan_***************",
      syncFrequency: "realtime",
      isEnabled: true,
      errorCount: 3
    },
    {
      id: 4,
      name: "Active Directory",
      type: "Authentication",
      status: "connected",
      lastSync: "2025-08-19T04:35:00Z",
      endpoint: "ldap://ad.techcorp.com:389",
      apiKey: "ad_***************",
      syncFrequency: "hourly",
      isEnabled: true,
      errorCount: 0
    },
    {
      id: 5,
      name: "ServiceNow ITSM",
      type: "Service Management",
      status: "disconnected",
      lastSync: "2025-08-17T14:20:00Z",
      endpoint: "https://techcorp.service-now.com/api",
      apiKey: "snow_***************",
      syncFrequency: "daily",
      isEnabled: false,
      errorCount: 1
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [newIntegration, setNewIntegration] = useState({
    name: "",
    type: "",
    endpoint: "",
    apiKey: "",
    syncFrequency: "daily",
    isEnabled: true
  });

  const integrationTypes = [
    { value: "ERP System", label: "ERP System" },
    { value: "Procurement Platform", label: "Procurement Platform" },
    { value: "Mobile Application", label: "Mobile Application" },
    { value: "Authentication", label: "Authentication Service" },
    { value: "Service Management", label: "Service Management" },
    { value: "Database", label: "External Database" },
    { value: "API Gateway", label: "API Gateway" }
  ];

  const syncFrequencyOptions = [
    { value: "realtime", label: "Real-time" },
    { value: "hourly", label: "Every Hour" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "manual", label: "Manual Only" }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'text-success';
      case 'error':
        return 'text-error';
      case 'disconnected':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return 'CheckCircle';
      case 'error':
        return 'AlertCircle';
      case 'disconnected':
        return 'XCircle';
      default:
        return 'Clock';
    }
  };

  const formatLastSync = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return date?.toLocaleDateString();
    }
  };

  const handleTestConnection = async (integration) => {
    setTestingConnection(integration?.id);
    
    // Simulate API test
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    const success = Math.random() > 0.3; // 70% success rate for demo
    
    setIntegrations(prev =>
      prev?.map(int =>
        int?.id === integration?.id
          ? {
              ...int,
              status: success ? 'connected' : 'error',
              lastSync: success ? new Date()?.toISOString() : int?.lastSync,
              errorCount: success ? 0 : int?.errorCount + 1
            }
          : int
      )
    );
    
    setTestingConnection(null);
  };

  const handleToggleIntegration = (id) => {
    setIntegrations(prev =>
      prev?.map(int =>
        int?.id === id
          ? {
              ...int,
              isEnabled: !int?.isEnabled,
              status: !int?.isEnabled ? int?.status : 'disconnected'
            }
          : int
      )
    );
  };

  const handleAddIntegration = () => {
    if (newIntegration?.name && newIntegration?.type && newIntegration?.endpoint) {
      const integration = {
        id: Date.now(),
        ...newIntegration,
        status: 'disconnected',
        lastSync: null,
        errorCount: 0
      };
      setIntegrations(prev => [...prev, integration]);
      setNewIntegration({
        name: "",
        type: "",
        endpoint: "",
        apiKey: "",
        syncFrequency: "daily",
        isEnabled: true
      });
      setShowAddModal(false);
    }
  };

  const handleEditIntegration = (integration) => {
    setEditingIntegration(integration);
    setNewIntegration({ ...integration });
    setShowAddModal(true);
  };

  const handleUpdateIntegration = () => {
    setIntegrations(prev =>
      prev?.map(int =>
        int?.id === editingIntegration?.id ? { ...newIntegration, id: editingIntegration?.id } : int
      )
    );
    setEditingIntegration(null);
    setNewIntegration({
      name: "",
      type: "",
      endpoint: "",
      apiKey: "",
      syncFrequency: "daily",
      isEnabled: true
    });
    setShowAddModal(false);
  };

  const handleDeleteIntegration = (id) => {
    if (window.confirm('Are you sure you want to delete this integration? This action cannot be undone.')) {
      setIntegrations(prev => prev?.filter(int => int?.id !== id));
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingIntegration(null);
    setNewIntegration({
      name: "",
      type: "",
      endpoint: "",
      apiKey: "",
      syncFrequency: "daily",
      isEnabled: true
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Integration Management</h3>
          <p className="text-sm text-muted-foreground">Configure external system connections and data synchronization</p>
        </div>
        <Button
          variant="default"
          onClick={() => setShowAddModal(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Add Integration
        </Button>
      </div>
      {/* Integration Cards */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {integrations?.map((integration) => (
          <div key={integration?.id} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Zap" size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-card-foreground">{integration?.name}</h4>
                  <p className="text-sm text-muted-foreground">{integration?.type}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className={`flex items-center space-x-1 ${getStatusColor(integration?.status)}`}>
                  <Icon name={getStatusIcon(integration?.status)} size={16} />
                  <span className="text-sm font-medium capitalize">{integration?.status}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Endpoint:</span>
                <span className="font-mono text-xs text-card-foreground truncate max-w-48" title={integration?.endpoint}>
                  {integration?.endpoint}
                </span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Sync Frequency:</span>
                <span className="font-medium text-card-foreground capitalize">{integration?.syncFrequency}</span>
              </div>
              
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Last Sync:</span>
                <span className="font-medium text-card-foreground">
                  {integration?.lastSync ? formatLastSync(integration?.lastSync) : 'Never'}
                </span>
              </div>
              
              {integration?.errorCount > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Errors:</span>
                  <span className="font-medium text-error">{integration?.errorCount} recent</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <div className="flex items-center space-x-3">
                <Checkbox
                  checked={integration?.isEnabled}
                  onChange={() => handleToggleIntegration(integration?.id)}
                  label="Enabled"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTestConnection(integration)}
                  disabled={testingConnection === integration?.id || !integration?.isEnabled}
                  loading={testingConnection === integration?.id}
                  iconName="Zap"
                  iconPosition="left"
                >
                  Test
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEditIntegration(integration)}
                  iconName="Edit"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteIntegration(integration?.id)}
                  iconName="Trash2"
                  className="text-error hover:text-error"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-card-foreground">
                {editingIntegration ? 'Edit Integration' : 'Add New Integration'}
              </h3>
              <Button variant="ghost" size="icon" onClick={closeModal}>
                <Icon name="X" size={20} />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Integration Name"
                  type="text"
                  value={newIntegration?.name}
                  onChange={(e) => setNewIntegration(prev => ({ ...prev, name: e?.target?.value }))}
                  placeholder="e.g., SAP ERP System"
                  required
                />
                
                <Select
                  label="Integration Type"
                  options={integrationTypes}
                  value={newIntegration?.type}
                  onChange={(value) => setNewIntegration(prev => ({ ...prev, type: value }))}
                  required
                />
              </div>

              <Input
                label="API Endpoint"
                type="url"
                value={newIntegration?.endpoint}
                onChange={(e) => setNewIntegration(prev => ({ ...prev, endpoint: e?.target?.value }))}
                placeholder="https://api.example.com/v1"
                description="Full URL to the API endpoint"
                required
              />

              <Input
                label="API Key / Token"
                type="password"
                value={newIntegration?.apiKey}
                onChange={(e) => setNewIntegration(prev => ({ ...prev, apiKey: e?.target?.value }))}
                placeholder="Enter API key or authentication token"
                description="This will be encrypted and stored securely"
              />

              <Select
                label="Sync Frequency"
                options={syncFrequencyOptions}
                value={newIntegration?.syncFrequency}
                onChange={(value) => setNewIntegration(prev => ({ ...prev, syncFrequency: value }))}
                description="How often should data be synchronized"
              />

              <Checkbox
                label="Enable Integration"
                description="Start synchronization immediately after saving"
                checked={newIntegration?.isEnabled}
                onChange={(e) => setNewIntegration(prev => ({ ...prev, isEnabled: e?.target?.checked }))}
              />
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
              <Button variant="outline" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={editingIntegration ? handleUpdateIntegration : handleAddIntegration}
                disabled={!newIntegration?.name || !newIntegration?.type || !newIntegration?.endpoint}
              >
                {editingIntegration ? 'Update Integration' : 'Create Integration'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationTab;